import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IMenuSummary, IMenuDetail } from "../../../types/menu";

/**
 * 📦 Estado global de menús
 *
 * - `menus`: lista general por módulo
 * - `menuDetail`: detalle de un solo menú
 */
export interface IMenuState {
  menus: IMenuSummary[];
  menuDetail: IMenuDetail | null;
}

/** 🧱 Estado inicial */
export const initialState: IMenuState = {
  menus: [],
  menuDetail: null,
};

export const menuSlice = createSlice({
  name: "menu",
  initialState,
  reducers: {
    // =====================================================
    // 🌐 CONSULTAS BASE
    // =====================================================

    /** Cargar la lista completa de menús (por módulo) */
    setMenus: (state, action: PayloadAction<IMenuSummary[]>) => {
      state.menus = action.payload;
    },

    /** Asignar el detalle del menú actual */
    setMenuDetail: (state, action: PayloadAction<IMenuDetail | null>) => {
      state.menuDetail = action.payload;
    },

    /** Limpiar detalle actual */
    clearMenuDetail: (state) => {
      state.menuDetail = null;
    },

    /** Reiniciar todo el estado */
    resetMenuState: () => initialState,

    // =====================================================
    // 🎯 ACTUALIZACIONES (solo afectan el detalle)
    // =====================================================

    updateMenuNameSuccess: (state, action: PayloadAction<{ newName: string }>) => {
      if (state.menuDetail) {
        state.menuDetail.name = action.payload.newName;
      }
    },

    updateMenuRouteSuccess: (state, action: PayloadAction<{ newRoute: string }>) => {
      if (state.menuDetail) {
        state.menuDetail.route = action.payload.newRoute;
      }
    },

    updateMenuOrderSuccess: (state, action: PayloadAction<{ newOrder: number }>) => {
      if (state.menuDetail) {
        state.menuDetail.orderIndex = action.payload.newOrder;
      }
    },

    updateMenuRolesSuccess: (state, action: PayloadAction<{ roles: string[] }>) => {
      if (state.menuDetail) {
        state.menuDetail.roles = action.payload.roles;
      }
    },

    // =====================================================
    // ✏️ ACTUALIZACIÓN LOCAL (inputs controlados)
    // =====================================================
    updateMenuField: (
      state,
      action: PayloadAction<{ field: keyof IMenuDetail; value: any }>
    ) => {
      const { field, value } = action.payload;
      if (state.menuDetail) {
        state.menuDetail = { ...state.menuDetail, [field]: value };
      }
    },
  },
});

// =====================================================
// 📤 EXPORTAR ACTIONS
// =====================================================
export const {
  setMenus,
  setMenuDetail,
  clearMenuDetail,
  resetMenuState,
  updateMenuNameSuccess,
  updateMenuRouteSuccess,
  updateMenuOrderSuccess,
  updateMenuRolesSuccess,
  updateMenuField,
} = menuSlice.actions;

// =====================================================
// 📤 EXPORTAR REDUCER
// =====================================================
export default menuSlice.reducer;
